import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CopyToClipboard from "react-copy-to-clipboard";
import { useDispatch } from "react-redux";
import {
  Group as GroupIcon,
  Share as ShareIcon,
  Email as EmailIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  Info as InfoIcon,
  Send as SendIcon,
  EuroSymbolSharp as EuroIcon,
  People as PeopleIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  AccountTree as AccountTreeIcon,
  TableChart as TableChartIcon,
} from "@mui/icons-material";
import Teachr from "../../../Objects/models/Teachr";
import TableLayout from "../../../Components/Ui/TableLayout";
import FormInput from "../../../Components/Ui/FormInput";
import ButtonField from "../../../Components/Ui/ButtonField";
import SponsorshipRepository from "../../../Repositories/SponsorshipRepository";
import SponsorshipModel from "../../../Objects/models/Sponsorship";
import { getSponsorship } from "../../../Redux/Actions/SponsorshipActions";
import { AppDispatch } from "../../../Redux/store";
import { SHOW_SUCCESS } from "../../../Redux/Reducers/MessageReducers";
import validateEmail from "../../../Validation/Email";
import { sponsor } from "../../../Utils/Images";
import { useTailwindBrand } from "../../../Components/shared";
import {
  ENV_VERIFIED_TRAINER,
  getActiveAppBrandConfig,
  getActiveBrandConfig,
  getActiveClientBrandConfig,
  getActivePrestationBrandConfig,
  getUriAppBrandConfig,
  isBrandActive,
} from "../../../config/environment.config";
import { capitalizeFirstLetter } from "../../../Utils/Function";
import { SponsorshipEnum, SponsorshipEnumColor } from "../../../Enum/SponsorshipEnum";
import { HeadCell } from "../../../Objects/interfaces/TableTypes";
import Settings from "../../../Components/Settings";
import ChildSponsorship from "./ChildSponsorship";
import MlmTreeView from "./Mlm/MlmTreeView";
import MlmLevelTable from "./Mlm/MlmLevelTable";

// ─── Données statiques sorties du composant ───────────────────────────────────

const STATUS_OPTIONS = [
  { value: SponsorshipEnum.STATUS_1_SEND,         label: SponsorshipEnumColor.STATUS_1_SEND.title },
  { value: SponsorshipEnum.STATUS_2_REGISTER,      label: SponsorshipEnumColor.STATUS_2_REGISTER.title },
  { value: SponsorshipEnum.STATUS_3_PROC_AE,       label: SponsorshipEnumColor.STATUS_3_PROC_AE.title },
  { value: SponsorshipEnum.STATUS_3_PROC_SAP,      label: SponsorshipEnumColor.STATUS_3_PROC_SAP.title },
  { value: SponsorshipEnum.STATUS_4_OK_PROC_AE,    label: SponsorshipEnumColor.STATUS_4_OK_PROC_AE.title },
  { value: SponsorshipEnum.STATUS_4_OK_PROC_SAP,   label: SponsorshipEnumColor.STATUS_4_OK_PROC_SAP.title },
  { value: SponsorshipEnum.STATUS_5_OK_CONDITION,  label: SponsorshipEnumColor.STATUS_5_OK_CONDITION.title },
  { value: SponsorshipEnum.STATUS_6_ACCOUNT_VALID, label: SponsorshipEnumColor.STATUS_6_ACCOUNT_VALID.title },
  { value: SponsorshipEnum.STATUS_7_ADD_STUDENT,   label: SponsorshipEnumColor.STATUS_7_ADD_STUDENT.title },
  { value: SponsorshipEnum.STATUS_8_FIRST_COURSE,  label: SponsorshipEnumColor.STATUS_8_FIRST_COURSE.title },
  { value: SponsorshipEnum.STATUS_9_COMPLET,       label: SponsorshipEnumColor.STATUS_9_COMPLET.title },
];

const HEAD_CELLS: HeadCell[] = [
  { id: 0, label: "Filleul",             align: "left",   disable: false, numeric: false },
  { id: 1, label: "Date",               align: "center", disable: false, numeric: false },
  { id: 2, label: "Élèves",             align: "center", disable: false, numeric: false },
  { id: 3, label: "Commission du mois", align: "center", disable: false, numeric: false },
  { id: 4, label: "Commission totale",  align: "center", disable: false, numeric: false },
  { id: 5, label: "Statut",             align: "center", disable: false, numeric: false },
  { id: 6, label: "Action",             align: "center", disable: false, numeric: false },
];

// ─── Sous-composant : résumé des métriques ────────────────────────────────────

interface MetricsSummaryProps {
  sponsorship: any;
  primaryHex: string;
  fontFamily: string;
}

const MetricsSummary = ({ sponsorship, primaryHex, fontFamily }: MetricsSummaryProps) => {
  if (!sponsorship?.totalSponsors) return null;
  return (
    <Box
      sx={{
        p: 2,
        mb: 3,
        borderRadius: 2,
        bgcolor: `${primaryHex}0d`,
        border: "1px solid",
        borderColor: `${primaryHex}30`,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <TrendingUpIcon sx={{ color: primaryHex, fontSize: 24 }} />
        <Typography variant="h6" fontWeight="600" fontFamily={fontFamily} sx={{ color: primaryHex }}>
          Résumé de vos parrainages
        </Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={6} md={3}>
          <Card variant="outlined" sx={{ p: 1.5, textAlign: "center", bgcolor: "background.paper", fontFamily: fontFamily }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, mb: 0.5 }}>
              <GroupIcon sx={{ color: "info.main", fontSize: 20 }} />
              <Typography variant="h6" fontWeight="bold" color="info.dark" sx={{ fontFamily: fontFamily }}>
                {sponsorship.totalSponsors}
              </Typography>
            </Box>
            <Typography variant="caption" color="info.600" sx={{ fontFamily: fontFamily }}>
              Filleuls
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ fontFamily: fontFamily }}>
              {sponsorship.activeSponsors} actifs
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card variant="outlined" sx={{ p: 1.5, textAlign: "center", bgcolor: "background.paper", fontFamily: fontFamily }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, mb: 0.5 }}>
              <EuroIcon sx={{ color: "success.main", fontSize: 20 }} />
              <Typography variant="h6" fontWeight="bold" color="success.dark" sx={{ fontFamily: fontFamily }}>
                {(sponsorship.totalFees ?? 0).toFixed(2)} €
              </Typography>
            </Box>
            <Typography variant="caption" color="success.600" sx={{ fontFamily: fontFamily }}>
              Total gagné
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ fontFamily: fontFamily }}>
              Moy : {(sponsorship.averageFeesPerSponsor ?? 0).toFixed(2)} €/filleul
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card variant="outlined" sx={{ p: 1.5, textAlign: "center", bgcolor: "background.paper", fontFamily: fontFamily }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, mb: 0.5 }}>
              <EuroIcon sx={{ color: "grey.600", fontSize: 20 }} />
              <Typography variant="h6" fontWeight="bold" color="text.primary" sx={{ fontFamily: fontFamily }}>
                {(sponsorship.totalMonthlyFees ?? 0).toFixed(2)} €
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: fontFamily }}>
              Ce mois
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ fontFamily: fontFamily }}>
              Gains actuels
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card variant="outlined" sx={{ p: 1.5, textAlign: "center", bgcolor: "background.paper", fontFamily: fontFamily }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, mb: 0.5 }}>
              <GroupIcon sx={{ color: "warning.main", fontSize: 20 }} />
              <Typography variant="h6" fontWeight="bold" color="warning.dark" sx={{ fontFamily: fontFamily }}>
                {sponsorship.totalStudents ?? 0}
              </Typography>
            </Box>
            <Typography variant="caption" color="warning.600" sx={{ fontFamily: fontFamily }}>
              {capitalizeFirstLetter(getActiveClientBrandConfig("s"))} total
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ fontFamily: fontFamily }}>
              Via vos filleuls
            </Typography>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

// ─── Composant principal ──────────────────────────────────────────────────────

const SponsorshipStep = (props: {
  teachr: Teachr;
  sponsorship: any;
  dataDispatch?: any;
  dataChild?: any;
}) => {
  const { brandConfig } = useTailwindBrand();
  const { teachr, sponsorship } = props;
  const url = `${getUriAppBrandConfig()}/${getActiveBrandConfig()}/register?=${teachr.invitationCode}`;
  const dispatch = useDispatch<AppDispatch>();
  const primaryHex = brandConfig.colors?.primary ?? "#1976d2";

  // ── UI state ────────────────────────────────────────────────────────────────
  const [open, setOpen] = useState(false);
  // Onglets MLM (MIP-252 / MIP-253) : réservés à Verified Trainer
  const showMlmTabs = isBrandActive(ENV_VERIFIED_TRAINER);
  const [activeTab, setActiveTab] = useState(0);
  const [mail, setMail] = useState("");
  const [isCopy, setIsCopy] = useState(false);
  const [isCopy2, setIsCopy2] = useState(false);
  const [load, setLoad] = useState(false);
  const [errorMail, setErrorMail] = useState({ error: false, msg: "" });

  // ── Pagination ──────────────────────────────────────────────────────────────
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  // ── Filtrage server-side ────────────────────────────────────────────────────
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Debounce : attend 400 ms après la dernière frappe avant d'appeler l'API
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Reset page quand le filtre statut change
  const handleStatusChange = useCallback((value: string) => {
    setStatusFilter(value);
    setPage(0);
  }, []);

  // ── dataDispatch pour l'API ─────────────────────────────────────────────────
  // Respecte ParamSponsorship : { teachr, page, limit, statut?, search? }
  const internalDataDispatch = useMemo(() => ({
    teachr,
    page,
    limit: rowsPerPage,
    search: debouncedSearch || undefined,
    statut: statusFilter || undefined,
  }), [teachr, page, rowsPerPage, debouncedSearch, statusFilter]);

  useEffect(() => {
    if (teachr?.id) {
      dispatch(getSponsorship(internalDataDispatch));
    }
  }, [teachr, internalDataDispatch, dispatch]);

  // Reset copy state après 1.5 s
  useEffect(() => {
    const t1 = setTimeout(() => setIsCopy(false), 1500);
    const t2 = setTimeout(() => setIsCopy2(false), 1500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [isCopy, isCopy2]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => { setOpen(false); setIsCopy(false); };

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const totalItems = sponsorship?.totalItems ?? sponsorship?.sponsorship?.length ?? 0;
  const hasActiveFilters = !!(debouncedSearch || statusFilter);

  // ── Envoi d'invitation par e-mail ───────────────────────────────────────────
  const validate = (): boolean => {
    const result = validateEmail(mail);
    if (result.isValid) {
      setErrorMail({ error: false, msg: result.message });
      return true;
    }
    setErrorMail({ error: true, msg: result.message });
    return false;
  };

  const handleSendMailSponsor = async () => {
    if (!validate()) return;
    setLoad(true);
    try {
      if (teachr?.invitationCode) {
        const model = new SponsorshipModel(teachr.invitationCode);
        model.mail = mail;
        const res = await SponsorshipRepository.addSponsorshipByTeachr(teachr, model);
        if (res) {
          setMail("");
          // Rafraîchit avec les filtres courants
          dispatch(getSponsorship(internalDataDispatch));
          dispatch({
            type: SHOW_SUCCESS,
            payload:
              "Votre e-mail contenant votre lien de parrainage vient d'être envoyé à votre filleul. Vous pourrez le retrouver dans votre tableau des filleuls et suivre l'avancement de son inscription.",
          });
          handleClose();
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoad(false);
    }
  };

  // ── Vue "aucun filleul" ─────────────────────────────────────────────────────
  const InvitationCard = () => (
    <Card
      variant="outlined"
      sx={{
        overflow: "hidden",
        borderRadius: 2,
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        border: "1px solid",
        borderColor: "grey.200",
        transition: "box-shadow 0.3s ease",
        "&:hover": { boxShadow: "0 8px 24px rgba(0,0,0,0.08)" },
      }}
    >
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          borderBottom: "1px solid",
          borderColor: "grey.200",
          background: `linear-gradient(135deg, ${primaryHex}14 0%, ${primaryHex}08 100%)`,
        }}
      >
        <Grid container alignItems="center" spacing={3}>
          <Grid item xs={12} md={8}>
            <Typography
              variant="h5"
              component="h2"
              fontWeight="bold"
              fontFamily={brandConfig.fontFamily}
              sx={{ color: primaryHex, mb: 1.5 }}
            >
              {isBrandActive(ENV_VERIFIED_TRAINER)
                ? "Développez votre équipe et votre réseau"
                : "Voyez encore plus loin, parrainez vos proches et cumulez les avantages"}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontFamily={brandConfig.fontFamily} sx={{ lineHeight: 1.6 }}>
              {isBrandActive(ENV_VERIFIED_TRAINER)
                ? "Parrainez, accompagnez et aidez d'autres coachs et éducateurs sportifs pour développer votre revenu passif et augmentez votre chiffre d'affaires !"
                : `À chaque ${getActivePrestationBrandConfig("", "effectué ", "effectuée ")} par vos filleuls, vous obtenez 20% H.T de la commission ${getActiveAppBrandConfig()} pendant une certaine durée.`}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: "flex", justifyContent: { xs: "center", md: "flex-end" } }}>
            <Box
              component="img"
              src={sponsor}
              alt="Parrainage"
              sx={{ width: { xs: 160, md: 220 }, height: { xs: 160, md: 220 }, objectFit: "contain" }}
            />
          </Grid>
        </Grid>
      </Box>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <TrendingUpIcon sx={{ color: primaryHex, fontSize: 24 }} />
          <Typography variant="h6" fontWeight="600" fontFamily={brandConfig.fontFamily} color="text.primary">
            {isBrandActive(ENV_VERIFIED_TRAINER)
              ? "Un plan de rémunération solide et puissant"
              : "Comment fonctionnent les commissions ?"}
          </Typography>
        </Box>
        {!isBrandActive(ENV_VERIFIED_TRAINER) && (
          <Box sx={{ p: 2, borderRadius: 1, bgcolor: `${primaryHex}14`, border: "1px solid", borderColor: `${primaryHex}40` }}>
            <Typography variant="subtitle2" fontWeight="600" fontFamily={brandConfig.fontFamily} sx={{ mb: 1, color: primaryHex }}>
              Pendant combien de temps vais-je percevoir ces commissions ?
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, color: "text.secondary", fontSize: "0.875rem" }}>
              <li><strong>1 à 4 filleuls</strong> : Commission pendant 12 mois</li>
              <li><strong>5 à 9 filleuls</strong> : Commission pendant 24 mois</li>
              <li><strong>10 à 14 filleuls</strong> : Commission pendant 36 mois</li>
            </Box>
          </Box>
        )}
        {isBrandActive(ENV_VERIFIED_TRAINER) && (
          <Box sx={{ p: 2, borderRadius: 1, bgcolor: "success.50", border: "1px solid", borderColor: "success.100" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography component="span" sx={{ fontSize: "1.25rem" }}>💼</Typography>
              <Typography variant="body2" fontWeight="600" color="success.dark">79% H.T sont reversés directement au coach</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography component="span" sx={{ fontSize: "1.25rem" }}>🎁</Typography>
              <Typography variant="body2" fontWeight="600" color="success.dark">10% H.T sont reversés aux parrains :</Typography>
            </Box>
            <Box component="ul" sx={{ m: 0, pl: 2.5, fontSize: "0.875rem", color: "success.700" }}>
              <li>8% H.T pour le parrain direct (niveau 1)</li>
              <li>2% H.T pour le parrain du parrain (niveau 2)</li>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Les 11% H.T restants reviennent à Verified Trainer. Barème en vigueur pour les cours déclarés depuis le 1er novembre 2026.
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  // ── Barre de recherche & filtre statut ─────────────────────────────────────
  const SearchBar = () => (
    <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <TextField
          sx={{ flex: 1, minWidth: 200 }}
          size="small"
          placeholder="Rechercher par prénom, nom ou e-mail"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
            endAdornment: searchInput ? (
              <IconButton size="small" onClick={() => { setSearchInput(""); setDebouncedSearch(""); }}>
                <ClearIcon fontSize="small" />
              </IconButton>
            ) : null,
          }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Statut</InputLabel>
          <Select
            value={statusFilter}
            label="Statut"
            onChange={(e) => handleStatusChange(e.target.value)}
          >
            <MenuItem value="">Tous les statuts</MenuItem>
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {hasActiveFilters && (
          <Tooltip title="Effacer les filtres">
            <IconButton
              size="small"
              color="error"
              onClick={() => { setSearchInput(""); setDebouncedSearch(""); setStatusFilter(""); }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Paper>
  );

  // ── Vue liste des filleuls ──────────────────────────────────────────────────
  const SponsorshipList = () => {
    const rows: any[] = sponsorship?.sponsorship ?? [];

    if (!sponsorship?.sponsorship) {
      return <Skeleton variant="rectangular" width="100%" height={300} sx={{ borderRadius: 2 }} />;
    }

    if (rows.length === 0 && !hasActiveFilters) {
      return <InvitationCard />;
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <MetricsSummary
          sponsorship={sponsorship}
          primaryHex={primaryHex}
          fontFamily={brandConfig.fontFamily}
        />

        {sponsorship?.sponsorship?.length > 0 && (
          <Typography variant="body2" color="text.secondary" fontFamily={brandConfig.fontFamily} sx={{ mb: 1 }}>
            {isBrandActive(ENV_VERIFIED_TRAINER)
              ? "Développez votre équipe, créez un véritable revenu passif durable et gagnez en liberté, tout en boostant la réussite d'autres coachs et éducateurs sportifs"
              : `Parrainez un proche et profitez d'une récompense exclusive : recevez 20% des frais ${getActiveAppBrandConfig()} de gestion H.T sur tous les ${getActivePrestationBrandConfig("s déclarées", "", "", " déclarés")} par votre filleul, et ce, pendant une année entière !`}
          </Typography>
        )}

        <SearchBar />

        {hasActiveFilters && (
          <Box
            sx={{
              mb: 1,
              px: 2,
              py: 1,
              borderRadius: 1.5,
              bgcolor: "info.50",
              border: "1px solid",
              borderColor: "info.200",
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontFamily: brandConfig.fontFamily, 
            }}
          >
            <SearchIcon color="info" fontSize="small" />
            <Typography variant="body2" color="info.dark" sx={{ fontFamily: brandConfig.fontFamily }}>
              <strong>{totalItems}</strong> résultat{totalItems > 1 ? "s" : ""} trouvé{totalItems > 1 ? "s" : ""}
            </Typography>
          </Box>
        )}

        {rows.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              height: 200,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 2,
              border: "1px dashed",
              borderColor: "divider",
              bgcolor: "grey.50",
              gap: 1,
            }}
          >
            <Typography fontSize="2rem">🔍</Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ maxWidth: 320 }}>
              Aucun filleul ne correspond à votre recherche.
            </Typography>
          </Paper>
        ) : (
          <TableLayout
            headCells={HEAD_CELLS}
            page={page + 1}
            rowsPerPage={rowsPerPage}
            count={totalItems}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            size="small"
            stickyHeader
            paperSx={{ boxShadow: 2, borderRadius: 2 }}
            Children={
              <>
                {rows.map((s: any, key: number) => (
                  <ChildSponsorship
                    key={s.id ?? key}
                    sponsor={s}
                    dataDispatch={internalDataDispatch}
                  />
                ))}
              </>
            }
          />
        )}
      </Box>
    );
  };

  // ── Vue outil de parrainage (dialog) ────────────────────────────────────────
  const sponsorshipSteps = [
    {
      step: 1,
      title: "Partagez votre lien de parrainage",
      description: `Faites découvrir ${getActiveAppBrandConfig()} à vos proches qui souhaitent donner des ${getActivePrestationBrandConfig("s")}. Partagez votre lien unique pour leur faciliter l'inscription.`,
      color: "#1976d2",
      badgeBg: "primary.main",
      titleColor: "primary.dark",
    },
    {
      step: 2,
      title: "Votre proche s'inscrit",
      description: `Une fois inscrit, il peut enregistrer ses ${getActiveClientBrandConfig("s")} sur ${getActiveAppBrandConfig()} et déclarer ses ${getActivePrestationBrandConfig("s")} en toute simplicité.`,
      color: "#2e7d32",
      badgeBg: "success.main",
      titleColor: "success.dark",
    },
    {
      step: 3,
      title: "Recevez vos récompenses",
      description: `Recevez à la fin de chaque mois ${isBrandActive(ENV_VERIFIED_TRAINER) ? '8%' : '20%'} des frais de ${getActiveAppBrandConfig()} H.T sur les ${getActivePrestationBrandConfig("s")} qu'il donne pendant un an. Les gains sont versés automatiquement à la fin de chaque mois.`,
      color: "#616161",
      badgeBg: "grey.600",
      titleColor: "text.primary",
    },
  ];

  const InvitationTool = () => (
    <Card variant="outlined" sx={{ overflow: "hidden", borderRadius: 2, borderColor: "grey.200" }}>
      <Box sx={{ p: 3, borderBottom: "1px solid", borderColor: "grey.200", bgcolor: `${primaryHex}0d` }}>
        <Typography variant="h6" fontWeight="bold" fontFamily={brandConfig.fontFamily} sx={{ color: primaryHex }}>
          Comment parrainer un proche et profiter des avantages ?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontFamily: brandConfig.fontFamily }}>
          Suivez ces étapes simples pour commencer à parrainer
        </Typography>
      </Box>
      <CardContent sx={{ p: 3 }}>
        {/* Étapes */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <InfoIcon sx={{ color: primaryHex, fontSize: 24 }} />
            <Typography variant="h6" fontWeight="600" fontFamily={brandConfig.fontFamily}>
              Étapes du parrainage
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {sponsorshipSteps.map((item) => (
              <Box
                key={item.step}
                sx={{ display: "flex", alignItems: "flex-start", gap: 2, p: 2, borderRadius: 1, bgcolor: `${item.color}14`, border: "1px solid", borderColor: `${item.color}40` }}
              >
                <Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: item.badgeBg, color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold", fontSize: "0.875rem", flexShrink: 0 }}>
                  {item.step}
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 0.5, color: item.titleColor, fontFamily: brandConfig.fontFamily }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: brandConfig.fontFamily }}>
                    {item.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Outils */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <ShareIcon sx={{ color: primaryHex, fontSize: 24 }} />
            <Typography variant="h6" fontWeight="600" fontFamily={brandConfig.fontFamily}>
              Outils de parrainage
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ p: 2, borderRadius: 1, bgcolor: "grey.50", border: "1px solid", borderColor: "grey.200" }}>
              <Typography variant="caption" fontWeight="600" color="text.secondary" sx={{ display: "block", mb: 1, fontFamily: brandConfig.fontFamily }}>
                Lien de parrainage
              </Typography>
              <Grid item xs={12} sm>
                <FormInput
                  label="Lien de parrainage"
                  value={url}
                  readOnly
                  name="url"
                  className="flex-1"
                  endAdornment={
                    <CopyToClipboard text={url} onCopy={() => setIsCopy(true)}>
                      <Tooltip title={isCopy ? "Copié" : "Cliquez pour copier"}>
                        <span>
                          <ButtonField
                            label={isCopy ? "Copié" : "Copier"}
                            variant="contained"
                            size="medium"
                            color={isCopy ? "success" : "primary"}
                            startIcon={isCopy ? <CheckCircleIcon /> : <CopyIcon />}
                          />
                        </span>
                      </Tooltip>
                    </CopyToClipboard>
                  }
                />
              </Grid>
            </Box>
            <Box sx={{ p: 2, borderRadius: 1, bgcolor: "grey.50", border: "1px solid", borderColor: "grey.200" }}>
              <Typography variant="caption" fontWeight="600" color="text.secondary" sx={{ display: "block", mb: 1, fontFamily: brandConfig.fontFamily }}>
                Code de parrainage
              </Typography>
              <Grid item xs={12} sm>
                <FormInput
                  label="Code de parrainage"
                  value={teachr?.invitationCode ?? ""}
                  readOnly
                  name="code"
                  className="flex-1"
                  endAdornment={
                    <CopyToClipboard text={teachr?.invitationCode ?? ""} onCopy={() => setIsCopy2(true)}>
                      <Tooltip title={isCopy2 ? "Copié" : "Cliquez pour copier"}>
                        <span>
                          <ButtonField
                            label={isCopy2 ? "Copié" : "Copier"}
                            variant="contained"
                            size="medium"
                            color={isCopy2 ? "success" : "primary"}
                            startIcon={isCopy2 ? <CheckCircleIcon /> : <CopyIcon />}
                          />
                        </span>
                      </Tooltip>
                    </CopyToClipboard>
                  }
                />
              </Grid>
            </Box>
          </Box>
        </Box>

        {/* Envoi par e-mail */}
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <EmailIcon sx={{ color: primaryHex, fontSize: 24 }} />
            <Typography variant="h6" fontWeight="600" fontFamily={brandConfig.fontFamily}>
              Envoyer par e-mail
            </Typography>
          </Box>
          <Box sx={{ p: 2, borderRadius: 1, bgcolor: "grey.50", border: "1px solid", borderColor: "grey.200" }}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm>
                <FormInput
                  label="Adresse e-mail"
                  name="mail"
                  type="email"
                  value={mail}
                  onChange={(e) => setMail(e.target.value)}
                  placeholder="Ex : johnxxx@gmail.com"
                  error={errorMail.error ? errorMail.msg : undefined}
                  endAdornment={
                    <ButtonField
                      label="Envoyer"
                      variant="contained"
                      startIcon={<SendIcon />}
                      onClick={handleSendMailSponsor}
                      loading={load}
                    />
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  // ── Rendu ───────────────────────────────────────────────────────────────────

  // VT, réseau vide : on affiche la page d'invitation avec un appel à parrainer
  // (l'onglet Filleuls n'existant plus pour cette marque).
  const mlmEmptyState = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <InvitationCard />
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <ButtonField label="Parrainer un coach" variant="contained" onClick={handleOpen} />
      </Box>
    </Box>
  );

  return (
    <Settings
      title={isBrandActive(ENV_VERIFIED_TRAINER) ? "Équipe et réseau" : "Parrainage"}
      icon={<PeopleIcon sx={{ color: primaryHex, fontSize: 28 }} />}
    >
      <Grid item xs={12}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            p: 2,
            mb: 2,
            borderRadius: 1,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "grey.200",
            fontFamily: brandConfig.fontFamily,
          }}
        >
          <Typography variant="body2" color="text.secondary" fontFamily={brandConfig.fontFamily}>
            {open
              ? "Vous êtes sur l'outil de parrainage."
              : showMlmTabs
                ? "Consultez votre réseau et vos commissions."
                : "Consultez votre liste de filleuls et vos commissions."}
          </Typography>
          <ButtonField
            label={open ? (showMlmTabs ? "Retour à mon réseau" : "Retour à ma liste") : "Parrainer un proche"}
            variant={open ? "outlined" : "contained"}
            onClick={open ? handleClose : handleOpen}
          />
        </Box>
      </Grid>
      {showMlmTabs ? (
        // Verified Trainer : pas de liste de filleuls, uniquement Arbre + Tableau
        open ? (
          <Grid item xs={12}>
            <InvitationTool />
          </Grid>
        ) : (
          <>
            <Grid item xs={12}>
              <Tabs
                value={activeTab}
                onChange={(_, value) => setActiveTab(value)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                TabIndicatorProps={{ style: { backgroundColor: primaryHex } }}
                sx={{ mb: 2, borderBottom: "1px solid", borderColor: "divider" }}
              >
                <Tab
                  icon={<AccountTreeIcon />}
                  iconPosition="start"
                  label="Arbre"
                  sx={{ fontFamily: brandConfig.fontFamily, textTransform: "none", "&.Mui-selected": { color: primaryHex, fontWeight: 600 } }}
                />
                <Tab
                  icon={<TableChartIcon />}
                  iconPosition="start"
                  label="Tableau"
                  sx={{ fontFamily: brandConfig.fontFamily, textTransform: "none", "&.Mui-selected": { color: primaryHex, fontWeight: 600 } }}
                />
              </Tabs>
            </Grid>
            <Grid item xs={12}>
              {activeTab === 0 ? (
                <MlmTreeView teachr={teachr} emptyState={mlmEmptyState} />
              ) : (
                <MlmLevelTable teachr={teachr} emptyState={mlmEmptyState} />
              )}
            </Grid>
          </>
        )
      ) : (
        // Teach'r / Déclarons : parrainage 1 niveau inchangé
        <Grid item xs={12}>
          {open ? <InvitationTool /> : <SponsorshipList />}
        </Grid>
      )}
    </Settings>
  );
};

export default SponsorshipStep;
